import React, { useEffect, useState } from "react";
import { CalendarOff, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { fmtDate, todayISO } from "@/lib/format";
import { PageHeader, Loading, Field } from "@/components/common";
import { DataTable, Btn, TextInput, Select, Textarea } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { Modal } from "@/components/Modal";

const TYPES = ["Annual Leave", "Sick Leave", "Public Holiday", "Training Day", "Company Day", "Other"];

export default function Leave() {
  const { user } = useAuth();
  const canManage = can(user, "manage_annual_leave");
  const [rows, setRows] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ consultant_id: "", leave_type: "Annual Leave", start_date: todayISO(), end_date: todayISO(), notes: "" });

  const load = async () => { setLoading(true); try { const { data } = await api.get("/leave"); setRows(data); } finally { setLoading(false); } };
  useEffect(() => { api.get("/consultants").then((r) => setConsultants(r.data)); load(); }, []);

  const save = async () => {
    if (!form.consultant_id) return toast.error("Select a consultant");
    try { const { data } = await api.post("/leave", form); toast.success(`Leave recorded${data.conflicting_visits ? ` — ⚠ ${data.conflicting_visits} scheduled visit(s) now conflict` : ""}`); setModal(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const approve = async (l) => { try { await api.post(`/leave/${l.id}/approve`); toast.success("Approved"); load(); } catch (e) { toast.error(apiError(e)); } };

  const columns = [
    { key: "consultant_name", label: "Consultant", className: "font-medium text-slate-800" },
    { key: "leave_type", label: "Type" },
    { key: "start_date", label: "From", render: (r) => fmtDate(r.start_date) },
    { key: "end_date", label: "To", render: (r) => fmtDate(r.end_date) },
    { key: "approval_status", label: "Status", render: (r) => <StatusBadge status={r.approval_status} /> },
    { key: "actions", label: "", align: "right", render: (r) => (canManage && r.approval_status === "Pending" ? <Btn onClick={() => approve(r)} className="py-1 px-2" data-testid={`approve-leave-${r.id}`}><Check size={14} /> Approve</Btn> : null) },
  ];

  return (
    <div>
      <PageHeader title="Leave & Availability" subtitle="Annual leave, sick days, training and unavailability — respected by the scheduler" icon={CalendarOff}
        actions={canManage && <Btn onClick={() => setModal(true)} data-testid="new-leave-btn"><Plus size={16} /> Record Leave</Btn>} />
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <Loading /> : <DataTable columns={columns} rows={rows} rowTestId="leave-row" />}
      </div>
      <Modal open={modal} onOpenChange={setModal} title="Record leave / unavailability"
        footer={<><Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn><Btn onClick={save} data-testid="save-leave-btn">Save</Btn></>}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Consultant" required className="col-span-2"><Select value={form.consultant_id} onChange={(e) => setForm({ ...form, consultant_id: e.target.value })}><option value="">Select</option>{consultants.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>
          <Field label="Type"><Select value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })}>{TYPES.map((t) => <option key={t}>{t}</option>)}</Select></Field>
          <Field label="Status"><Select value={form.approval_status || "Approved"} onChange={(e) => setForm({ ...form, approval_status: e.target.value })}><option>Approved</option><option>Pending</option></Select></Field>
          <Field label="Start date"><TextInput type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
          <Field label="End date"><TextInput type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
          <Field label="Notes" className="col-span-2"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        </div>
      </Modal>
    </div>
  );
}
